import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { IOrderDetailByName } from "../models/IOrderDetail";
import { getOrderDetailsByOrderName } from "../services/orderService";
import { getRestaurantById } from "../../services/restaurant";
import { IRestaurantDetail } from "../../models/IRestaurantDetail";
import { orderStatuses } from "../../utils/Util";

/* ── Durum görünümü ── */

// Util.orderStatuses'taki task değerleri: 0 alındı, 2 hazırlanıyor, 3 yolda, 1 teslim, 4 iptal
const STATUS_STYLE: Record<number, { label: string; icon: string; color: string; bg: string; border: string; hint: string }> = {
  0: { label: "Sipariş alındı", icon: "bi-bag-check-fill", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe",
       hint: "Restoran siparişi henüz hazırlamaya başlamadı. Gecikme varsa restoranı arayarak teyit edebilirsiniz." },
  2: { label: "Hazırlanıyor", icon: "bi-fire", color: "#b45309", bg: "#fffbeb", border: "#fde68a",
       hint: "Sipariş restoranda hazırlanıyor. Müşteriye tahmini süre için restoranla görüşebilirsiniz." },
  3: { label: "Yolda", icon: "bi-bicycle", color: "#c2410c", bg: "#fff7ed", border: "#fed7aa",
       hint: "Sipariş kuryede. Gecikme şikâyetinde restoran üzerinden kuryeye ulaşılabilir." },
  1: { label: "Teslim edildi", icon: "bi-check-circle-fill", color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0",
       hint: "Sipariş teslim edilmiş görünüyor. Eksik / yanlış ürün ya da kalite şikâyeti olabilir." },
  4: { label: "İptal edildi", icon: "bi-x-circle-fill", color: "#b91c1c", bg: "#fef2f2", border: "#fecaca",
       hint: "Sipariş iptal edilmiş. Ödeme yapıldıysa müşteri iade sorabilir." },
};

const STEPS: { task: number; label: string; icon: string }[] = [
  { task: 0, label: "Alındı", icon: "bi-bag-check" },
  { task: 2, label: "Hazırlanıyor", icon: "bi-fire" },
  { task: 3, label: "Yolda", icon: "bi-bicycle" },
  { task: 1, label: "Teslim", icon: "bi-house-check" },
];

const ERROR_TEXT: Record<string, string> = {
  OrderNotFound: "Bu kodla bir sipariş bulunamadı. Kodu müşteriden tekrar teyit edin.",
  AmbiguousOrderName: "Bu kod birden fazla siparişle eşleşti. Müşteriden sipariş tarihini ve restoranı isteyin.",
  OrderAccessDenied: "Bu siparişi görüntüleme yetkiniz yok.",
};

export function orderErrorMessage(err: unknown): string {
  const code = (err as { response?: { data?: { code?: string }[] } })?.response?.data?.[0]?.code;
  return (code && ERROR_TEXT[code]) || "Sipariş bulunamadı veya bir hata oluştu.";
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "az önce";
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} sa ${mins % 60} dk önce`;
  const days = Math.floor(hours / 24);
  return `${days} gün önce`;
}

const money = (n: number, cur: string) => `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

const sectionTitle: CSSProperties = {
  fontSize: "0.67rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em",
  display: "flex", alignItems: "center", gap: 6,
};

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={`${label} kopyala`}
      onClick={() => {
        navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        }).catch(() => {});
      }}
      style={{
        border: "none", background: copied ? "#dcfce7" : "#eef2ff", color: copied ? "#15803d" : "#4f46e5",
        borderRadius: 7, padding: "2px 7px", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", flexShrink: 0,
      }}
    >
      <i className={`bi ${copied ? "bi-check2" : "bi-copy"}`} /> {copied ? "Kopyalandı" : "Kopyala"}
    </button>
  );
}

function InfoCard({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <div style={{ flex: "1 1 220px", minWidth: 0, border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "11px 13px", background: "#fff" }}>
      <div style={{ ...sectionTitle, marginBottom: 6 }}>
        <i className={`bi ${icon}`} style={{ color: "#6366f1" }} />{title}
      </div>
      {children}
    </div>
  );
}

/* ── Araç ── */

/* ── Sipariş detayı (sorgulama aracı ve "Siparişi Görüntüle" ortak kullanır) ── */

export function OrderDetailView({ order, orderCode }: { order: IOrderDetailByName; orderCode: string }) {
  const result = order;
  const searchedCode = orderCode.trim().replace(/^#+/, "").toUpperCase();
  const [restaurant, setRestaurant] = useState<IRestaurantDetail | null>(null);
  const [restaurantLoading, setRestaurantLoading] = useState(false);

  useEffect(() => {
    if (!order.restourantId) return;
    setRestaurantLoading(true);
    getRestaurantById(order.restourantId)
      .then((r) => setRestaurant(r.data))
      .catch(() => setRestaurant(null))
      .finally(() => setRestaurantLoading(false));
  }, [order.restourantId]);

  const cur = result.currency?.currencyDisplayText ?? "";
  const items = result.foodPreOrder ?? [];
  const totalItems = items.reduce((s, i) => s + i.foodQuantity, 0);

  // Fiyat dökümü: liste fiyatı → ürün indirimleri → (promosyon / teslimat farkı) → ödenen
  const listTotal = items.reduce((s, i) => s + i.unitPrice * i.foodQuantity, 0);
  const linesTotal = items.reduce((s, i) => s + i.lineTotal, 0);
  const itemDiscount = listTotal - linesTotal;
  const otherDiff = result.orderPrice - linesTotal;

  const task = orderStatuses[result.orderStatusId]?.task;
  const status = task !== undefined ? STATUS_STYLE[task] : undefined;
  const statusName = result.orderStatus?.orderStatusNameTr?.replace(/\.$/, "") ?? "Bilinmiyor";
  const stepIndex = STEPS.findIndex((s) => s.task === task);

  return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Kod + durum */}
          <div style={{
            borderRadius: 14, border: `1.5px solid ${status?.border ?? "#e5e7eb"}`,
            background: status?.bg ?? "#f9fafb", padding: "12px 14px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}>
                    #{searchedCode}
                  </span>
                  <CopyButton value={searchedCode} label="Sipariş kodunu" />
                </div>
                <div style={{ fontSize: "0.74rem", color: "#6b7280", marginTop: 3 }}>
                  <i className="bi bi-clock me-1" />{formatDate(result.orderDate)} · {timeAgo(result.orderDate)}
                </div>
              </div>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20,
                background: "#fff", border: `1.5px solid ${status?.border ?? "#e5e7eb"}`,
                color: status?.color ?? "#374151", fontWeight: 800, fontSize: "0.82rem", whiteSpace: "nowrap",
              }}>
                <i className={`bi ${status?.icon ?? "bi-circle"}`} />{statusName}
              </span>
            </div>

            {/* Aşamalar */}
            {task === 4 ? (
              <div style={{ marginTop: 12, fontSize: "0.8rem", fontWeight: 700, color: "#b91c1c", display: "flex", alignItems: "center", gap: 6 }}>
                <i className="bi bi-x-octagon-fill" />Bu sipariş iptal edilmiş.
              </div>
            ) : stepIndex >= 0 && (
              <div style={{ display: "flex", alignItems: "flex-start", marginTop: 14 }}>
                {STEPS.map((s, i) => {
                  const done = i <= stepIndex;
                  const current = i === stepIndex;
                  return (
                    <div key={s.task} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", minWidth: 0 }}>
                      {i > 0 && (
                        <span style={{
                          position: "absolute", top: 13, right: "50%", width: "100%", height: 3, borderRadius: 3,
                          background: i <= stepIndex ? status?.color ?? "#6366f1" : "#e5e7eb",
                        }} />
                      )}
                      <span style={{
                        position: "relative", width: 28, height: 28, borderRadius: "50%",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem",
                        background: done ? status?.color ?? "#6366f1" : "#fff",
                        color: done ? "#fff" : "#9ca3af",
                        border: `2px solid ${done ? status?.color ?? "#6366f1" : "#e5e7eb"}`,
                        boxShadow: current ? `0 0 0 4px ${status?.border ?? "#e0e7ff"}` : "none",
                      }}>
                        <i className={`bi ${s.icon}`} />
                      </span>
                      <span style={{ fontSize: "0.68rem", fontWeight: current ? 800 : 600, color: done ? "#111827" : "#9ca3af", marginTop: 5, textAlign: "center" }}>
                        {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {status && (
              <div style={{ marginTop: 12, fontSize: "0.76rem", color: "#4b5563", background: "rgba(255,255,255,0.7)", borderRadius: 10, padding: "8px 10px", display: "flex", gap: 7 }}>
                <i className="bi bi-lightbulb-fill" style={{ color: status.color, flexShrink: 0, marginTop: 1 }} />
                <span>{status.hint}</span>
              </div>
            )}
          </div>

          {/* Restoran + müşteri */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <InfoCard icon="bi-shop" title="Restoran">
              {restaurantLoading ? (
                <span className="spinner-border spinner-border-sm text-secondary" />
              ) : restaurant ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: "0.8rem", color: "#374151" }}>
                  <div style={{ fontWeight: 800, color: "#111827", fontSize: "0.86rem" }}>{restaurant.restourantName}</div>
                  {restaurant.restourantPhoneNo && (
                    <a href={`tel:${restaurant.restourantPhoneNo}`} style={{ color: "#4f46e5", textDecoration: "none", fontWeight: 700 }}>
                      <i className="bi bi-telephone-fill me-1" />{restaurant.restourantPhoneNo}
                    </a>
                  )}
                  {restaurant.restourantManagerName && (
                    <span><i className="bi bi-person me-1" style={{ color: "#9ca3af" }} />{restaurant.restourantManagerName}</span>
                  )}
                  {restaurant.restourantAddress && (
                    <span style={{ fontSize: "0.74rem", color: "#6b7280" }}><i className="bi bi-geo-alt me-1" />{restaurant.restourantAddress}</span>
                  )}
                  {restaurant.restourantEstDeliveryTime ? (
                    <span style={{ fontSize: "0.74rem", color: "#6b7280" }}><i className="bi bi-stopwatch me-1" />Ort. teslim süresi {restaurant.restourantEstDeliveryTime} dk</span>
                  ) : null}
                </div>
              ) : (
                <div style={{ fontSize: "0.76rem", color: "#9ca3af" }}>Restoran bilgisi alınamadı.</div>
              )}
            </InfoCard>

            <InfoCard icon="bi-person-circle" title="Müşteri">
              {result.customerId ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: "0.72rem", color: "#374151", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {result.customerId}
                    </span>
                    <CopyButton value={result.customerId} label="Müşteri id'sini" />
                  </div>
                  <span style={{ fontSize: "0.72rem", color: "#9ca3af" }}>Sohbetteki müşteriyle aynı kişi olduğunu kontrol edin.</span>
                </div>
              ) : (
                <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>Hesapsız (misafir) sipariş.</div>
              )}
            </InfoCard>
          </div>

          {/* Sipariş notu */}
          {result.orderNote?.trim() && (
            <div style={{
              padding: "9px 13px", borderRadius: 10,
              background: "#fffbeb", border: "1.5px solid #fde68a",
              fontSize: "0.8rem", color: "#92400e",
              display: "flex", alignItems: "flex-start", gap: 7,
            }}>
              <i className="bi bi-chat-left-text-fill" style={{ color: "#f59e0b", flexShrink: 0, marginTop: 1 }} />
              <span><strong>Müşteri notu:</strong> {result.orderNote}</span>
            </div>
          )}

          {/* Ürünler */}
          <div style={sectionTitle}>
            <i className="bi bi-bag-fill" style={{ color: "#6366f1" }} />
            Sipariş Kalemleri · {totalItems} adet
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map((item) => {
              const hasDiscount = item.discountedUnitPrice < item.unitPrice;
              const hasNote = item.foodOrderNote?.trim();
              const hasUpgrade = item.menuUpgradeNotes?.trim();

              return (
                <div key={item.id} style={{ border: "1.5px solid #e5e7eb", borderRadius: 12, background: "#fff", overflow: "hidden" }}>
                  <div style={{ padding: "10px 12px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                      background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, fontSize: "0.82rem", color: "#fff",
                    }}>
                      {item.foodQuantity}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.86rem", color: "#111827" }}>{item.food?.foodNameTr ?? "Ürün"}</div>
                      <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: 1 }}>
                        Birim {money(item.unitPrice, cur)}
                        {hasDiscount && <span style={{ color: "#16a34a", fontWeight: 700 }}> → {money(item.discountedUnitPrice, cur)} (kampanya)</span>}
                      </div>
                      {item.food?.foodContents?.trim() && (
                        <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: 1 }}>İçerik: {item.food.foodContents}</div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      {hasDiscount && (
                        <div style={{ fontSize: "0.68rem", color: "#9ca3af", textDecoration: "line-through" }}>
                          {money(item.unitPrice * item.foodQuantity, cur)}
                        </div>
                      )}
                      <div style={{ fontSize: "0.88rem", fontWeight: 800, color: hasDiscount ? "#16a34a" : "#1d4ed8" }}>
                        {money(item.lineTotal, cur)}
                      </div>
                    </div>
                  </div>
                  {(hasNote || hasUpgrade) && (
                    <div style={{ borderTop: "1px solid #f1f5f9", padding: "6px 12px", background: "#fafafa", display: "flex", flexDirection: "column", gap: 3 }}>
                      {hasNote && (
                        <div style={{ fontSize: "0.7rem", color: "#6b7280", display: "flex", alignItems: "flex-start", gap: 5 }}>
                          <i className="bi bi-pencil" style={{ color: "#a5b4fc", flexShrink: 0, marginTop: 1 }} />
                          {item.foodOrderNote}
                        </div>
                      )}
                      {hasUpgrade && (
                        <div style={{ fontSize: "0.7rem", color: "#6b7280", display: "flex", alignItems: "flex-start", gap: 5 }}>
                          <i className="bi bi-arrow-up-circle" style={{ color: "#6ee7b7", flexShrink: 0, marginTop: 1 }} />
                          {item.menuUpgradeNotes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Fiyat dökümü */}
          <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 5, fontSize: "0.8rem", color: "#4b5563" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Ürünler (liste fiyatı)</span><span>{money(listTotal, cur)}</span>
              </div>
              {itemDiscount > 0.005 && (
                <div style={{ display: "flex", justifyContent: "space-between", color: "#16a34a" }}>
                  <span>Ürün kampanyaları</span><span>−{money(itemDiscount, cur)}</span>
                </div>
              )}
              {Math.abs(otherDiff) > 0.005 && (
                <div style={{ display: "flex", justifyContent: "space-between", color: otherDiff < 0 ? "#16a34a" : "#4b5563" }}>
                  <span>{otherDiff < 0 ? "Promosyon kodu indirimi" : "Ek ücret (teslimat vb.)"}</span>
                  <span>{otherDiff < 0 ? "−" : "+"}{money(Math.abs(otherDiff), cur)}</span>
                </div>
              )}
            </div>
            <div style={{
              padding: "10px 14px",
              background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "rgba(255,255,255,0.85)" }}>Ödenen Tutar</span>
              <span style={{ fontWeight: 800, fontSize: "1rem", color: "#fff" }}>{money(result.orderPrice, cur)}</span>
            </div>
          </div>
        </div>
  );
}

/* ── Araç ── */

export default function OrderQueryTool({ compact }: { compact: boolean }) {
  const [query, setQuery] = useState("");
  const [searchedCode, setSearchedCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IOrderDetailByName | null>(null);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    const q = query.trim().replace(/^#+/, "");
    if (!q) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await getOrderDetailsByOrderName(q);
      setResult(res.data);
      setSearchedCode(q);
    } catch (err) {
      setError(orderErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const pad = compact ? "14px" : "20px";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: pad }}>
      {/* Açıklama */}
      <div style={{
        background: "#f0f4ff", border: "1.5px solid #c7d2fe", borderRadius: 12,
        padding: "10px 14px", fontSize: "0.76rem", color: "#4338ca",
        display: "flex", alignItems: "flex-start", gap: 8,
      }}>
        <i className="bi bi-info-circle-fill" style={{ marginTop: 1, flexShrink: 0 }} />
        <span>
          Müşterinin elindeki sipariş kodunu girin (ör. <strong>#MU-8b95a6a0</strong>). Baştaki #, boşluklar ve büyük/küçük harf fark etmez.
        </span>
      </div>

      {/* Arama */}
      <div style={{ display: "flex", gap: 8, flexWrap: compact ? "wrap" : "nowrap" }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder="Sipariş kodu girin…"
          autoFocus
          style={{
            flex: 1, minWidth: 0, borderRadius: 10, border: "1.5px solid #e0e7ff",
            padding: "10px 14px", fontSize: compact ? "0.95rem" : "0.88rem",
            color: "#111827", outline: "none", background: "#f8faff",
            fontFamily: "inherit",
          }}
        />
        <button
          onClick={search}
          disabled={loading || !query.trim()}
          style={{
            background: query.trim() && !loading ? "linear-gradient(135deg, #6366f1, #4f46e5)" : "#e5e7eb",
            border: "none", borderRadius: 10,
            padding: compact ? "10px 0" : "9px 20px",
            width: compact ? "100%" : "auto",
            fontWeight: 700, fontSize: compact ? "0.95rem" : "0.85rem",
            color: query.trim() && !loading ? "#fff" : "#9ca3af",
            cursor: query.trim() && !loading ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            boxShadow: query.trim() && !loading ? "0 3px 12px rgba(99,102,241,0.3)" : "none",
            transition: "all 0.15s", flexShrink: 0,
          }}
        >
          {loading
            ? <><span className="spinner-border spinner-border-sm" /> Sorgulanıyor…</>
            : <><i className="bi bi-search" /> Sorgula</>}
        </button>
      </div>

      {/* Hata */}
      {error && (
        <div style={{
          padding: "10px 14px", borderRadius: 10,
          background: "#fef2f2", border: "1.5px solid #fecaca",
          color: "#991b1b", fontSize: "0.82rem",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <i className="bi bi-exclamation-circle-fill" />
          {error}
        </div>
      )}

      {result && <OrderDetailView key={searchedCode} order={result} orderCode={searchedCode} />}
    </div>
  );
}
